
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { parseStoredDateTime } from "@/utils/timeUtils";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, Clock, FileText, Mail, MapPin, Phone, Home, Check, X, Loader2, CheckCircle, Star, DollarSign, MessageSquare } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BookingCompletionDialog, BookingCompletionData } from "./BookingCompletionDialog";

interface UnifiedBookingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  booking: any;
  onSuccess?: () => void;
  onAccept?: (bookingId: number) => Promise<void>;
  onDecline?: (bookingId: number, reason?: string) => Promise<void>;
  onComplete?: (bookingId: number, completionData: BookingCompletionData) => Promise<void>;
  isAdminView?: boolean;
  actionInProgress?: number | null;
  progressValue?: number;
  showSuccessAnimation?: boolean;
}

export const UnifiedBookingDialog = ({
  isOpen,
  onClose,
  booking,
  onSuccess,
  onAccept,
  onDecline,
  onComplete,
  isAdminView = false,
  actionInProgress = null,
  progressValue = 0,
  showSuccessAnimation = false,
}: UnifiedBookingDialogProps) => {
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mapsApiKey, setMapsApiKey] = useState<string | null>(null);
  const [isLoadingKey, setIsLoadingKey] = useState(false);
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] = useState(false);
  const [completionData, setCompletionData] = useState<any>(null);
  const { toast } = useToast();

  // Auto-close dialog when action completes (including completion)
  useEffect(() => {
    if (booking?.id && actionInProgress === booking.id && showSuccessAnimation && !isCompletionDialogOpen) {
      // When success animation is shown, prepare to close the dialog
      const timer = setTimeout(() => {
        onClose();
      }, 1500); // Match the animation duration from the hooks
      
      return () => clearTimeout(timer);
    }
  }, [booking, actionInProgress, showSuccessAnimation, onClose, isCompletionDialogOpen]);

  // Fetch Google Maps API key when dialog opens
  useEffect(() => {
    const fetchMapsApiKey = async () => {
      if (booking?.customer_address) {
        setIsLoadingKey(true);
        try {
          console.log('Fetching Google Maps API key from edge function');
          const { data, error } = await supabase.functions.invoke('get-maps-api-key');
          
          if (error) {
            console.error('Error fetching Maps API key:', error);
            return;
          }
          
          console.log('Successfully received Maps API key');
          setMapsApiKey(data.apiKey);
        } catch (error) {
          console.error('Failed to fetch Maps API key:', error);
        } finally {
          setIsLoadingKey(false);
        }
      }
    };

    if (isOpen && booking?.customer_address) {
      fetchMapsApiKey();
    }
  }, [isOpen, booking]);

  // Fetch completion data for completed bookings
  useEffect(() => {
    const fetchCompletionData = async () => {
      if (booking?.status === 'completed' && booking?.id) {
        try {
          const { data, error } = await supabase
            .from('booking_completions')
            .select('*')
            .eq('booking_id', booking.id)
            .single();

          if (error) {
            console.error('Error fetching completion data:', error);
            return;
          }

          console.log('📊 Completion data fetched:', data);
          setCompletionData(data);
        } catch (error) {
          console.error('Failed to fetch completion data:', error);
        }
      }
    };

    if (isOpen && booking?.status === 'completed') {
      fetchCompletionData();
    }
  }, [isOpen, booking]);

  const renderStars = (rating: number | null | undefined) => {
    const safeRating = rating || 0;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= safeRating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-muted-foreground">
          ({safeRating}/5)
        </span>
      </div>
    );
  };

  const handleCancelBooking = async () => {
    if (!booking) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status: 'cancelled' })
        .eq('id', booking.id);

      if (error) throw error;
      
      toast({
        title: "Booking cancelled",
        description: "Your booking has been cancelled successfully",
      });
      
      setIsConfirmingCancel(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptClick = async () => {
    if (onAccept && booking) {
      await onAccept(booking.id);
    }
  };

  const handleDeclineClick = () => {
    setShowDeclineReason(true);
  };

  const handleConfirmDecline = async () => {
    if (onDecline && booking) {
      await onDecline(booking.id, declineReason);
      setShowDeclineReason(false);
    }
  };

  const handleCompleteClick = () => {
    setIsCompletionDialogOpen(true);
  };

  const handleCompletionSubmit = async (completionData: BookingCompletionData) => {
    if (onComplete && booking) {
      await onComplete(booking.id, completionData);
      setIsCompletionDialogOpen(false);
    }
  };

  const getGoogleMapsUrl = (address: string, mapLink?: string) => {
    if (mapLink && mapLink.trim()) {
      return mapLink;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const getMapEmbedUrl = (address: string, mapLink?: string, apiKey?: string) => {
    if (!apiKey) return null;
    
    if (mapLink && mapLink.trim()) {
      // Check if mapLink contains coordinates (lat,lng format)
      const coordinatePattern = /^-?\d+\.?\d*,-?\d+\.?\d*$/;
      if (coordinatePattern.test(mapLink.trim())) {
        return `https://www.google.com/maps/embed/v1/view?key=${apiKey}&center=${encodeURIComponent(mapLink)}&zoom=15`;
      }
      
      // If mapLink is a Google Maps URL, use it directly
      if (mapLink.includes('google.com/maps') || mapLink.includes('maps.app.goo.gl')) {
        return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(mapLink)}`;
      }
      
      // Otherwise treat it as a search query
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(mapLink)}`;
    }
    
    return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(address)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if this booking has an action in progress
  const isActioningCurrentBooking = actionInProgress === booking?.id;

  // Get progress step message based on progress value
  const getProgressMessage = () => {
    if (progressValue < 30) return "Updating booking status...";
    if (progressValue >= 30 && progressValue < 50) return "Processing update...";
    if (progressValue >= 50 && progressValue < 70) return "Creating records...";
    if (progressValue >= 70 && progressValue < 90) return "Sending notifications...";
    return "Almost done...";
  };

  if (!booking) {
    return null;
  }

  const startTime = parseStoredDateTime(booking.start_time);
  const endTime = parseStoredDateTime(booking.end_time);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !isActioningCurrentBooking && !open && onClose()}>
        <DialogContent className="sm:max-w-[500px] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {isAdminView ? "Booking Details" : "Your Booking"}
            </DialogTitle>
            <DialogDescription>
              {isAdminView 
                ? "View and manage booking information, customer details, and booking status" 
                : "View your booking details including time, service, and status information"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4 relative">
            {isConfirmingCancel ? (
              <div className="space-y-4">
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  <AlertDescription>
                    Are you sure you want to cancel this booking? This action cannot be undone.
                  </AlertDescription>
                </Alert>
                
                <div className="flex gap-3 mt-4">
                  <Button 
                    variant="destructive" 
                    className="flex-1" 
                    onClick={handleCancelBooking}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      "Yes, Cancel Booking"
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setIsConfirmingCancel(false)}
                    disabled={isLoading}
                  >
                    No, Keep Booking
                  </Button>
                </div>
              </div>
            ) : showDeclineReason ? (
              <div className="space-y-4">
                <Label htmlFor="decline-reason">Reason for declining (optional)</Label>
                <Textarea
                  id="decline-reason"
                  placeholder="Please enter a reason for declining this booking..."
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex gap-3 pt-3">
                  <Button 
                    variant="destructive" 
                    className="flex-1" 
                    onClick={handleConfirmDecline}
                  >
                    Confirm Decline
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setShowDeclineReason(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {showSuccessAnimation && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 animate-fade-in">
                    <div className="p-8 rounded-full bg-green-100 flex items-center justify-center animate-scale-in">
                      <CheckCircle className="h-16 w-16 text-green-600" />
                    </div>
                  </div>
                )}
              
                {isActioningCurrentBooking && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Processing your request...</p>
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    </div>
                    <Progress value={progressValue} className="h-2" />
                    <p className="text-xs text-gray-500">
                      {getProgressMessage()}
                    </p>
                  </div>
                )}
              
                {isAdminView && booking.customer_name && (
                  <div className="flex items-start space-x-3">
                    <FileText className="w-5 h-5 mt-0.5 text-gray-500" />
                    <div>
                      <p className="font-medium">{booking.customer_name}</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">
                      {format(startTime, "MMMM d, yyyy")}
                    </p>
                    <p className="font-medium">
                      {format(startTime, "h:mm a")} - {format(endTime, "h:mm a")}
                    </p>
                  </div>
                </div>

                {/* Created at timestamp */}
                {booking.created_at && (
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Created at:</p>
                      <p className="font-medium">
                        {format(new Date(booking.created_at), "MMM d, yyyy h:mm a")}
                      </p>
                    </div>
                  </div>
                )}

                {booking.service_name && (
                  <div className="p-3 bg-blue-50 rounded-md">
                    <p className="font-medium text-blue-800">{booking.service_name}</p>
                    <p className="text-sm text-blue-600">
                      Status: <span className="font-medium capitalize">{booking.status}</span>
                    </p>
                  </div>
                )}

                {booking.decline_reason && (
                  <div className="flex items-start space-x-3 bg-red-50 p-3 rounded-md">
                    <FileText className="w-5 h-5 mt-0.5 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Declined reason:</p>
                      <p className="text-sm text-red-900">{booking.decline_reason}</p>
                    </div>
                  </div>
                )}

                {/* Completion Details Section - only show for completed bookings */}
                {booking.status === 'completed' && completionData && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        Completion Details
                      </h3>
                      
                      {/* Rating */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Vendor Review</p>
                        {renderStars(completionData.rating)}
                      </div>

                      {/* Payment */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              Ksh {completionData.payment_amount ? Number(completionData.payment_amount).toFixed(2) : "0.00"}
                            </p>
                            <p className="text-sm text-muted-foreground">Payment Amount</p>
                          </div>
                        </div>
                        <Badge 
                          variant={completionData.payment_status === 'paid' ? 'default' : 'secondary'}
                          className={completionData.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-800 hover:bg-green-100' 
                            : 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                          }
                        >
                          {completionData.payment_status || "pending"}
                        </Badge>
                      </div>

                      {/* Comment */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-muted-foreground" />
                          <p className="text-sm font-medium">Vendor Comment</p>
                        </div>
                        <div className="bg-muted p-3 rounded-md">
                          <p className="text-sm">
                            {completionData.comment || "No comment provided"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

                {booking.customer_email && (
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <a href={`mailto:${booking.customer_email}`} className="text-sm text-blue-600 hover:underline">
                      {booking.customer_email}
                    </a>
                  </div>
                )}
                
                {booking.customer_phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-500" />
                    <a href={`tel:${booking.customer_phone}`} className="text-sm text-blue-600 hover:underline">
                      {booking.customer_phone}
                    </a>
                  </div>
                )}
                
                {booking.customer_address && (
                  <>
                    <div className="flex items-start space-x-3">
                      <Home className="w-5 h-5 mt-0.5 text-gray-500" />
                      <p className="text-sm">{booking.customer_address}</p>
                    </div>
                    
                    <div className="flex flex-col space-y-2">
                      <a 
                        href={getGoogleMapsUrl(booking.customer_address, booking.customer_map_link)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-sm text-blue-600 hover:underline"
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        View on Google Maps
                      </a>
                      
                      <div className="bg-gray-100 rounded-md h-[150px] w-full overflow-hidden">
                        {isLoadingKey ? (
                          <div className="flex items-center justify-center h-full text-gray-500">
                            Loading map...
                          </div>
                        ) : mapsApiKey ? (
                          <iframe 
                            title="Location Map"
                            width="100%" 
                            height="100%" 
                            frameBorder="0" 
                            src={getMapEmbedUrl(booking.customer_address, booking.customer_map_link, mapsApiKey)}
                            allowFullScreen
                            className="rounded-md"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full text-gray-500">
                            Map not available
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {booking.note && (
                  <div className="flex items-start space-x-3 bg-amber-50 p-3 rounded-md">
                    <FileText className="w-5 h-5 mt-0.5 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Note:</p>
                      <p className="text-sm text-amber-900">{booking.note}</p>
                    </div>
                  </div>
                )}

                {!isAdminView && booking.status === 'new' && onAccept && onDecline && !isActioningCurrentBooking && (
                  <div className="flex gap-3 pt-3">
                    <Button 
                      variant="outline" 
                      className="flex-1 border-green-600 text-green-600 hover:bg-green-50"
                      onClick={handleAcceptClick}
                      disabled={isActioningCurrentBooking}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accept
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                      onClick={handleDeclineClick}
                      disabled={isActioningCurrentBooking}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                  </div>
                )}

                {!isAdminView && booking.status === 'accepted' && onComplete && !isActioningCurrentBooking && (
                  <div className="flex gap-3 pt-3">
                    <Button 
                      variant="default" 
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={handleCompleteClick}
                      disabled={isActioningCurrentBooking}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Completed
                    </Button>
                  </div>
                )}

                {!isAdminView && booking.status !== "cancelled" && (
                  <div className="pt-3">
                    <Button 
                      variant="outline" 
                      className="w-full border-red-600 text-red-600 hover:bg-red-50"
                      onClick={() => setIsConfirmingCancel(true)}
                    >
                      Cancel Booking
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Completion Dialog */}
      {booking && (
        <BookingCompletionDialog
          isOpen={isCompletionDialogOpen}
          onClose={() => setIsCompletionDialogOpen(false)}
          onComplete={handleCompletionSubmit}
          booking={booking}
          loading={actionInProgress === booking.id}
          initialAmount={booking.service_price || booking.price || 0}
          currencySymbol={booking.currency || "Ksh"}
        />
      )}
    </>
  );
};
